import { FC, useState } from 'react';
import { Grid, Box, Typography, useMediaQuery, Theme, Button } from '@mui/material';
import { AudioPlayer, FileUpload, MuiIconButton, RichText } from '@/components';
import VideoItem from '@/screens/Memories/components/VideoContent/VideoItem';
import MediaPopup from '@/components/MediaPopup';
import TextArea from '@/components/TextArea';
import { cdn_url, getFileType } from '@/utils';
import { useDispatch, useSelector } from 'react-redux';
import MuiTextField3 from '@/components/TextField3';
import InspirationModal from '@/components/InspirationalModal';
import { authSelector } from '@/store/selectors';

interface MediaUploadStepProps {
  values: any;
  errors: any;
  touched: any;
  handleOnTouched: any;
  handleChange: (e: any) => void;
  setisAllowed?: any;
  formikSubmit?: () => void;
  setFieldValue: (name: string, value: any) => void;
}

const MediaUploadStep: FC<MediaUploadStepProps> = ({
  errors,
  values,
  handleOnTouched,
  touched,
  handleChange,
  setFieldValue,
  setisAllowed,
  formikSubmit,
}) => {
  const [showRichText, setShowRichText] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const user = useSelector(authSelector);
  const [showCard, setShowCard] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ media: any; type: string; index: any } | null>(null);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  console.log("media files", values?.mediaFiles)
  const handleOpen = (media: any, index: any) => {
    setSelectedMedia({ media, type: media?.file?.type, index });
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedMedia(null);
    setOpen(false);
  };

  const validateMediaFile = (file: File, type: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (type === 'image') {
        const img = document.createElement('img') as HTMLImageElement;
        img.src = URL.createObjectURL(file);
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      } else if (type === 'video') {
        const media = document.createElement(type) as HTMLMediaElement;
        media.src = URL.createObjectURL(file);
        media.onloadeddata = () => resolve(true);
        media.onerror = () => resolve(false);
      } else {
        resolve(false);
      }
    });
  };

  const handleFileUpload = async (_: any, files: File | FileList) => {
    if (!files) {
      return;
    }

    const inputFiles = files instanceof File ? [files] : Array.from(files);
    const validFiles: any[] = [];
    const previews: string[] = [];

    const alreadyHasFeaturedImage = values.mediaFiles.some((media: any) => {
      const isImage =
        media.file?.type?.startsWith('image') ||
        (typeof media.url === 'string' && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(media.url)) ||
        (typeof media.preview === 'string' && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(media.preview));
      return media.isFeatured && isImage;
    });

    let featuredAssigned = alreadyHasFeaturedImage;

    for (const file of inputFiles) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      let mime = file.type;

      if (!mime || mime === '') {
        if (['mp3', 'wav', 'aac', 'm4a', 'ogg'].includes(extension)) {
          mime = `audio/${extension}`;
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension)) {
          mime = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
        } else if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
          mime = `video/${extension}`;
        }
      }

      const isImage = mime.startsWith('image');
      const isVideo = mime.startsWith('video');
      const isAudio = mime.startsWith('audio');

      let fileType = '';
      if (isImage) fileType = 'image';
      else if (isVideo) fileType = 'video';
      else if (isAudio) fileType = 'audio';

      if (!fileType) {
        console.warn(`Unsupported file:`, file);
        continue;
      }

      const shouldBeFeatured = isImage && !featuredAssigned;
      if (shouldBeFeatured) featuredAssigned = true;

      const originalName =
        file.name
          .split('/')
          .pop()
          ?.replace(/\.[^/.]+$/, '') || `file_${validFiles.length}`;
      const timestamp = Date.now() + validFiles.length * 1000;
      const safeTitle = values.title || 'untitled';
      const generatedFileName = isVideo
        ? `${timestamp}_${originalName}${extension ? '.' + extension : ''}`
        : `${timestamp}_${originalName}${isImage ? '.webp' : extension ? '.' + extension : ''}`;

      const timestampedFile = new File([file], generatedFileName, { type: file.type });

      const previewURL = URL.createObjectURL(timestampedFile);

      validFiles.push({
        file: timestampedFile,
        preview: previewURL,
        isFeatured: shouldBeFeatured,
      });

      previews.push(previewURL);
    }

    if (validFiles.length > 0) {
      setPreviewUrls((prev) => [...prev, ...previews]);
      setFieldValue('mediaFiles', [...values.mediaFiles, ...validFiles]);
    }
  };

  const handleDeleteMedia = (media: any) => {
    if (media?.fileName) {
      const updatedMediaFiles = values.mediaFiles.filter((_: any) => _.fileName !== media?.fileName);
      setFieldValue('mediaFiles', updatedMediaFiles);
    } else {
      const updatedMediaFiles = values.mediaFiles.filter((_: any) => _.preview !== media?.preview);
      setFieldValue('mediaFiles', updatedMediaFiles);
    }
  };

  const hasUploadedMedia = values.mediaFiles && values.mediaFiles.length > 0;
  const [featuredIndex, setFeaturedIndex] = useState<number | null>(null);

  const handleFeaturedSelect = (media: any) => {
    const mediaId = media?.fileName || media?.preview;
    const updatedMediaFiles = values.mediaFiles.map((item: any) => {
      const itemId = item?.fileName || item?.preview;
      return {
        ...item,
        isFeatured: itemId === mediaId ? !item.isFeatured : false,
      };
    });

    setFieldValue('mediaFiles', updatedMediaFiles);
    setFeaturedIndex(media.isFeatured ? null : media.preview);
  };

  // const isAudioFile = (media: any) => {
  //   const type = media?.file?.type || '';
  //   const url = media?.url || media?.preview || '';
  //   const extension = url.split('.').pop()?.toLowerCase();

  //   const audioExtensions = [
  //     'mp3',
  //     'wav',
  //     'ogg',
  //     'm4a',
  //     'aac',
  //     'flac',
  //     'aiff',
  //     'alac',
  //     'amr',
  //     'wma',
  //     '3gp',
  //     'webm',
  //     'opus',
  //   ];

  //   return type.startsWith('audio') || (extension && audioExtensions.includes(extension));
  // };

  const handleClick = () => {
    setShowCard((prev) => !prev);
  };
  const getExt = (s?: string) => {
    const clean = (s || '').split('?')[0].split('#')[0];
    const last = clean.split('/').pop() || '';
    const dot = last.lastIndexOf('.');
    return dot >= 0 ? last.slice(dot + 1).toLowerCase() : '';
  };

  const resolveSrc = (media: any, cdn_url: string) => {
    const hasDBPath =
      typeof media?.fileName === 'string' &&
      (media.fileName.startsWith('stories/') ||
        media.fileName.startsWith('videos/') ||
        media.fileName.startsWith('audios/'));
    const fromDB = hasDBPath ? `${cdn_url}${media.fileName}` : null;
    const fromThumb = media?.thumb ? `${cdn_url}${media.thumb}` : null;
    return media?.preview || media?.url || fromDB || fromThumb || null;
  };
  const isAudioFile = (media: any): boolean => {
    const mime = media?.file?.type || '';
    if (mime.startsWith('audio/')) return true; // primary
    if (mime.startsWith('video/')) return false; // definitely not audio

    // no/unknown MIME → fall back to extension from best src or fileName
    const src = resolveSrc(media, cdn_url) || media?.fileName || media?.file?.name || media?.name || '';
    const ext = getExt(src);
    // typical audio exts; NOTE: we do NOT blanket-include webm here unless hinted by MIME
    return /^(mp3|wav|m4a|aac|flac|ogg|oga|opus)$/i.test(ext);
  };

  const isVideoFile = (media: any): boolean => {
    const mime = media?.file?.type || '';
    if (mime.startsWith('video/')) return true; // primary
    if (mime.startsWith('audio/')) return false; // definitely not video

    const src = resolveSrc(media, cdn_url) || media?.fileName || media?.file?.name || media?.name || '';
    const ext = getExt(src);
    // treat webm as video ONLY when MIME says video or when no MIME but you want default video;
    // here we choose conservative: webm is NOT auto-video; needs video/ MIME.
    return /^(mp4|mov|mkv|avi|wmv|m4v)$/i.test(ext);
  };

  const isImageFile = (media: any): boolean => {
    const mime = media?.file?.type || '';
    if (mime.startsWith('image/')) return true;
    if (mime.startsWith('video/') || mime.startsWith('audio/')) return false;

    const src = resolveSrc(media, cdn_url) || media?.fileName || media?.file?.name || media?.name || '';
    const ext = getExt(src);
    return /^(png|jpg|jpeg|webp|gif|heic|heif|bmp|svg)$/i.test(ext);
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' width='100%' gap={1} mb={1} marginLeft={'0.85rem'}>
      {hasUploadedMedia && (
        <>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={'1rem'}
            sx={{
              width: 'fit-content',
              mx: 'auto',
              '& > *': { m: 0 },
            }}>
            {/* Photos & Videos Upload */}
            <Box display='flex' justifyContent='center'>
              <FileUpload
                height='100px'
                name='photosAndVideos'
                multiple
                setisAllowed={setisAllowed}
                onChange={handleFileUpload}
                buttonLayout={true}
                placeholder='Photos & Videos'
                acceptedFormats='image/*,video/*'
                icon='/icons/upload'
                buttonwidth='170px'
              />
            </Box>

            {/* Audio Upload */}
            <Box display='flex' justifyContent='center'>
              <FileUpload
                height='100px'
                name='audio'
                mediaType='audio'
                setisAllowed={setisAllowed}
                onChange={handleFileUpload}
                buttonLayout={true}
                placeholder='Audio'
                acceptedFormats='audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.aiff,.alac,.amr,.wma,.3gp,.webm,.opus,.mp4,audio/mpeg,audio/x-m4a,audio/mp4,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/flac,audio/aiff,audio/x-aiff,audio/alac,audio/amr,audio/x-ms-wma,audio/3gpp,audio/webm,audio/opus'
                icon='/icons/audio-file'
                buttonwidth='85px'
              />
            </Box>
          </Box>
        </>
      )}

      <Box display='flex' flexDirection='column' alignItems='center' mt={2}>
        <Typography
          fontSize='13px'
          color='#FEFEFE'
          mb={'3px'}
          lineHeight={'18px'}
          align='left'
          sx={{ width: isMobile ? '310px' : '350px', fontFamily: '"Inter", sans-serif !important' }}>
          Name this memory
        </Typography>
        <MuiTextField3
          id='title'
          name='title'
          onBlur={() => handleOnTouched('title')}
          onChange={handleChange}
          value={values.title}
          placeholder='Title'
          errorMessage={touched.title ? errors.title : ''}
          error={touched.title && !!errors.title}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '46px',
              borderRadius: '5px',
              background: '#fff',
              '& fieldset': {
                border: 'none',
              },
              '& .MuiOutlinedInput-input': {
                padding: 0,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '15px',
              },
            },
            width: isMobile ? '310px' : '350px',
          }}
        />
      </Box>

      <Box display='flex' flexDirection={'column'} justifyContent='center' mb={1}>
        <Typography
          fontSize='1rem'
          mb={1}
          color='#fff'
          align='center'
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            textDecoration: 'underline',
          }}>
          Need Inspiration?
        </Typography>

        <InspirationModal showCard={showCard} setShowCard={setShowCard} />
      </Box>

      {!hasUploadedMedia && (
        <>
          <Box display='flex' justifyContent='center' width='100%' mb={1}>
            <FileUpload
              height='100px'
              name='mediaFiles'
              buttonLayout={false}
              setisAllowed={setisAllowed}
              multiple
              onChange={handleFileUpload}
              placeholder='Upload Photos & Videos'
              icon='/icons/upload-white.svg'
              acceptedFormats='image/*,video/*'
            />
          </Box>

          <Box display='flex' justifyContent='center' width='100%' mb={'12px'}>
            <FileUpload
              height='100px'
              name='audio'
              buttonLayout={false}
              setisAllowed={setisAllowed}
              mediaType='audio'
              multiple
              onChange={handleFileUpload}
              placeholder='Upload or Record Audio'
              icon='/icons/sound-light.svg'
              acceptedFormats='audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.aiff,.alac,.amr,.wma,.3gp,.webm,.opus,.mp4,audio/mpeg,audio/x-m4a,audio/mp4,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/flac,audio/aiff,audio/x-aiff,audio/alac,audio/amr,audio/x-ms-wma,audio/3gpp,audio/webm,audio/opus'
            />
          </Box>
        </>
      )}

      {/* Media Preview Section */}
      <Grid
        container
        width={isMobile ? '350px' : '92%'}
        display='flex'
        spacing={isMobile ? 1 : 2}
        columnSpacing={isMobile ? 0 : 2}
        sx={{ marginright: '1rem' }}>
        {Array.isArray(values.mediaFiles) &&
          values.mediaFiles.length > 0 &&
          values.mediaFiles
            .filter((m: any) => !isAudioFile(m) && (isVideoFile(m) || isImageFile(m)))
            .map((media: any, index: number) => {
              // ---------- helpers to detect type & src ----------
              const getExt = (s: string) => {
                const clean = (s || '').split('?')[0].split('#')[0];
                const last = clean.split('/').pop() || '';
                const dot = last.lastIndexOf('.');
                return dot >= 0 ? last.slice(dot + 1).toLowerCase() : '';
              };

              const isVideoByExt = (s: string) => /^(mp4|mov|mkv|webm|avi|wmv|m4v)$/i.test(getExt(s));
              const isImageByExt = (s: string) => /^(png|jpg|jpeg|webp|gif|heic|heif|bmp|svg)$/i.test(getExt(s));

              const mime = media?.file?.type || '';
              const isVideoByMime = mime.startsWith('video/');
              const isImageByMime = mime.startsWith('image/');

              const hasDBPath =
                typeof media?.fileName === 'string' &&
                (media.fileName.startsWith('stories/') || media.fileName.startsWith('videos/'));

              const srcFromDB = hasDBPath
                ? `${cdn_url}${media.fileName}`
                : media?.thumb
                ? `${cdn_url}${media.thumb}`
                : null;

              const src =
                // priority: explicit preview/url from UI
                media?.preview || media?.url || srcFromDB || null;

              const kindIsVideo =
                isVideoByMime ||
                (src ? isVideoByExt(src) : false) ||
                (hasDBPath && media.fileName.startsWith('videos/'));

              const kindIsImage =
                isImageByMime ||
                (src ? isImageByExt(src) : false) ||
                (hasDBPath && media.fileName.startsWith('stories/'));

              const gridKey = media?.fileName || media?.url || media?.preview || media?.file?.name || `media-${index}`;

              return (
                <Grid item xs={6} sm={4} md={3} key={gridKey}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '210px',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      marginBottom: '10px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpen(media, index)}>
                    {kindIsVideo ? (
                      <VideoItem
                        hideControls
                        item={media}
                        name={`video-${index}`}
                        elementSelected={null}
                        handleSelectVideo={() => {}}
                        thumbnail={false}
                      />
                    ) : (
                      kindIsImage && (
                        <Box
                          width='100%'
                          height='100%'
                          borderRadius='8px'
                          display='flex'
                          justifyContent='center'
                          alignItems='center'
                          bgcolor='rgba(0, 0, 0, 0.4)'
                          position='relative'>
                          <img
                            src={src || ''}
                            alt='Uploaded'
                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                          />
                        </Box>
                      )
                    )}

                    {media?.isFeatured && typeof media?.isFeatured === 'boolean' && (kindIsImage || kindIsVideo) && (
                      <Button
                        variant={media.isFeatured ? 'contained' : 'outlined'}
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFeaturedSelect(media);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: media.isFeatured ? 'primary.main' : 'rgba(0,0,0,0.5)',
                          color: '#000',
                          fontSize: '10px',
                          padding: '2px 6px',
                          minWidth: 'auto',
                          borderRadius: '12px',
                        }}>
                        {media.isFeatured ? '✓ Featured' : 'Feature'}
                      </Button>
                    )}
                  </Box>
                </Grid>
              );
            })}

        {values.mediaFiles?.filter(isAudioFile).map((file: any, index: number) => (
          <Grid item xs={12} sm={6} md={6} key={`audio-${index}`} sx={{ display: 'flex' }}>
            <AudioPlayer
              key={index}
              showDownload={true}
              audioData={file}
              name={file?.file?.name || file?.url?.split('/').pop()?.split('_').pop() || 'audio'}
              handleDeleteMedia={() => handleDeleteMedia(file)}
            />
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <TextArea
          name='richText'
          placeholder='Context. Makes. It. Real.
Share a text only story
-or-
Add details to your uploaded media'
          value={values.richText}
          onChange={(_event: any, value: any) => setFieldValue('richText', value)}
        />
      </Grid>

      {open && (
        <MediaPopup
          open={open}
          onClose={handleClose}
          handleDeleteMedia={() => handleDeleteMedia(selectedMedia?.media)}
          handleFeaturedSelect={() => handleFeaturedSelect(selectedMedia?.media)}
          mediaSrc={selectedMedia || ''}
          index={selectedMedia?.media?.preview}
          isFeatured={selectedMedia?.media?.isFeatured || false}
          isVideo={
            selectedMedia?.media?.file?.type?.startsWith('video') ||
            (selectedMedia?.media?.fileName && getFileType(selectedMedia?.media?.fileName) == 'video') ||
            false
          }
        />
      )}
    </Box>
  );
};

export default MediaUploadStep;
